import * as React from 'react'

import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { cn } from '~/client/src/shared/utils/cn'

import { SheetHeightMode } from './FullScreenPickerSheet'

interface IProps {
  title: string
  onClose: () => void
  /** Pinned to the bottom of the sheet (e.g. Cancel / Save). */
  footer?: React.ReactNode
  /**
   * Must match the host `FullScreenPickerSheet`'s `heightMode`. `fill` (default)
   * stretches the body to fill a tall sheet and pins the footer to the bottom;
   * `content` lets the body hug its content so the footer sits directly beneath.
   * Both scroll the body once it exceeds the sheet.
   */
  heightMode?: SheetHeightMode
  children: React.ReactNode
}

/**
 * Fills a FullScreenPickerSheet with the standard picker chrome: the shared
 * `ListSelector.Title` header (title left, close "X" right), a scrollable content
 * area, and an optional footer pinned to the bottom. Used by the resource-card
 * Controls editors when they surface as a sheet instead of a popover.
 */
function PickerSheetLayout({
  title,
  onClose,
  footer,
  heightMode = 'fill',
  children,
}: IProps) {
  const isContent = heightMode === 'content'
  return (
    <div
      className={cn(
        'tw:flex tw:min-h-0 tw:flex-col',
        !isContent && 'tw:h-full',
      )}
    >
      <ListSelector.Title title={title} onClose={onClose} />
      <div
        className={cn('tw:min-h-0 tw:overflow-auto', !isContent && 'tw:flex-1')}
      >
        {children}
      </div>
      {footer && (
        <div className="tw:shrink-0 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:px-3 tw:pt-3 tw:pb-safe-3">
          {footer}
        </div>
      )}
    </div>
  )
}

export default PickerSheetLayout
